/**
 * The note model's add-ons, each a switch beside the check while arranging starts from the model.
 *
 * The model is shown the tune and nothing else of the score, and is never fitted again. What it
 * has learnt since is an add-on apiece, fitted on top of it and apart from every other: one
 * tells it the harmony the score's accompaniment sounds under the tune, one the rhythm that
 * accompaniment moves in, one how much of that accompaniment is each player's own. Each is put on
 * or left off by itself, so a writing can be heard with any of them or none. Those three were
 * fitted apart, and on together they get in each other's way; so a fourth, Together, is one
 * add-on fitted on all three at once and on when each player's own share moves. It stands in
 * for the three, not beside them: putting it on puts them off and putting one of them on puts
 * it off (`switched`), because beside any of them it keeps less of the score than alone. It is
 * the one on until switched (`FOLLOW_ON`): on the held-out quartets it keeps more of the score's
 * harmony, bass, rhythm and shares than any setting of the three (docs/07, "The note model's
 * add-ons"). A fifth, Layers, is told all that Together is and where a player's share says an
 * earlier bar again, and was fitted with each of those now told and now withheld, so any of
 * them can be left out (`ArrangeLayers`); it too stands alone, in place of the other four. With
 * every layer told it follows the score a little less closely than Together, which is why
 * Together stays the one on. A sixth, More, is told all that Together is and asked, besides,
 * for more notes than the score gives each player, by an amount beside it (`ArrangeLayers`);
 * it stands alone as well. A seventh, Line, is told all that Together is and where each
 * player's own share goes note by note, by its pitch classes and its steps; it stands alone
 * too. How far whatever of those is on is put on is the strength, beside them. The model's notes are still its own, and still checked: an add-on tells it more, it
 * does not copy the score's notes.
 *
 * Those seven are fitted. Breath is held (`ARRANGE_HELD`): it tells the model nothing and has no
 * weights, and gives a player who sounds on breath a rest before a bar line once its line has
 * gone sixteen quarters without one, which the part's grammar keeps. So it is on beside any of
 * the seven or none: no switch of theirs puts it off, a preset and an answer in words leave it
 * as it stands, and the strength does not reach it. It is on until switched, as Together is
 * (`FOLLOW_ON`): bowed players are given nothing by it, so a quartet is written as without it,
 * and a wood-wind, a brass player or a voice under the tune is rested (docs/07, "A wind
 * player's breath, counted"). Spacing is held too: it keeps every player above the lowest part
 * within a fifth under the part above it and under the tune, which by itself costs some of the
 * score's bass (docs/07, "The spacing, held and counted"). Bass is held as well: it keeps the
 * lowest part on the score's bass, which is what Spacing costs (docs/07, "The score's bass,
 * held and counted"). Lowest is held too: it keeps the lowest part the lowest, no part over it
 * written under the score's bass, which is where Bass still lost the bass (docs/07, "The lowest
 * part, held and counted"). Present, Quarter and Release are held too, each reading the score's
 * bass by the quarter: the lowest part sounds where the parts over it are off that bass, the
 * lowest note it sounds in a quarter is on it, and a note of its ends where that bass moves
 * (docs/07, "The bass by the quarter, held and counted"). The six are on until switched, as
 * Together is and whoever plays (`FOLLOW_ON`): beside Together and Breath they leave a third
 * of the faults and keep nearly all of the score's bass, and a change of the players moves no
 * switch; a request sent over HTTP or the command line has them only where it names them
 * (docs/07, "The six held add-ons, on whoever plays"). Breath mark is the tune's
 * own breath and the one held add-on that is marked, not kept: the tune's notes are the
 * score's, so a blown player carrying it is marked a breath at a bar line and no note is
 * changed. It is on until switched, as Breath is: it gives a bowed tune nothing and changes no
 * note anybody plays (docs/07, "The tune's breath, marked and counted"). Marks is marked too:
 * the score's slurs, accents, staccato and plucking on the parts the model writes, which are
 * bare without it. It is on until switched as well, since it changes no note either (docs/07,
 * "The score's marks, held and counted"). Dynamics is marked as well: each part the model
 * writes at the level the score has its share of the accompaniment in, where without it every
 * part is at the tune's. It is the one held add-on off until switched: the balance rule weighs
 * a chord at the levels written, and at the score's own it faults chords it passed at one
 * level -- the quartet leaves 49 faults where it left 9 -- and the check, mending those,
 * changes notes (docs/07, "The score's dynamics, held and counted"). Over is a seating: a pianist
 * under a tune the model reads above where it sounds -- a cello's, a bassoon's -- plays where
 * the model writes it and not an octave under, its right hand over the tune's player. It is on
 * until switched: it holds a pianist beside such a tune and nobody else, every other roster
 * written note for note as without it, and a cello's pianist leaves 68 notes untaken where it
 * left 413 and keeps the score's bass in 95.9% of quarters for 84.5% (docs/07, "A pianist's
 * hands over a low tune, held and counted"); a harpist, an organist and a celesta's player are held as a pianist is. Octave is held: the parts over the lowest are kept
 * within reach of the highest octave the lowest player has of the score's bass, so a bass
 * singer or a bassoon is not left a tenth under the part over it. It is on until switched:
 * over three takes the thirteen rosters leave 260 faults where they left 435, four voices 43
 * for 174, and it costs the wind quintet and the brass a few faults and up to a point and a
 * half of the score's harmony (docs/07, "The bass's octave, held and counted"). Apart is a
 * held add-on that is a choice and not a hold: a wood-wind player would rather begin a
 * note off the pitch another wood-wind is sounding, among what every hold leaves it. It is on
 * until switched: it holds nothing where fewer than two wood-wind play, and the wind quartet
 * leaves 33 faults for 75 and the wind quintet 27 for 59, for about a point of the score's
 * harmony (docs/07, "The wood-wind apart, held and counted"). Duet is held: the voice written
 * last sings on a bar's first beat where two other voices alone would stand an empty fourth
 * or fifth apart on it, and beside one other voice would rather begin no note there that
 * makes one. It is on until switched: it holds nothing where fewer than two voices sing, and
 * over three takes four voices leave 25 faults for 43, 4 of the voices' rule where 21 were,
 * the score's harmony within a tenth of a point (docs/07, "Two voices alone, held and
 * counted"). Fifth is a choice as Apart is: a part would rather begin no note more than a fifth
 * under the part above it where nothing of the score's sounds under its share, nor more than a
 * fifth above the highest note of the part under it, where a note of the score's harmony is
 * among those left. It is on until switched: over three takes the thirteen rosters leave 106
 * faults where they left 169, 55 of spacing for 121, none of them more, the score's harmony and
 * bass as they were (docs/07, "The fifth chosen, held and counted"). Restate is marked, as
 * Marks is: a part holding a note over a bar line where the score changes level is marked the
 * new level on the notehead it is carried onto. It is on until switched: it changes no note,
 * and over three takes the thirteen rosters leave 88 faults where they left 106, 9 of balance
 * for 27 (docs/07, "The level restated, held and counted"). Top is held: the lowest part
 * begins no note above the lowest note the tune is sounding. It is on until switched: over
 * three takes the eleven rosters it holds leave 105 faults where they left 106, 2 of melody
 * for 11, and the wind quartet 26 for 24 (docs/07, "The tune on top, held and counted"). Third
 * is held: the voice over the lowest sings on a bar's first beat where the lowest voice and
 * one other would stand alone an empty fourth or fifth apart. It is on until switched: it
 * holds nothing where fewer than three voices sing, and over three takes four voices leave
 * none of their rule's faults where they left 3 (docs/07, "A third voice, held and counted").
 * Doubled is a choice as Fifth is: a part that may begin a note more than a fifth under the
 * part above it would rather begin one within the fifth on the tune's note or the score's
 * bass. It is on until switched: over three takes the thirteen rosters leave 74 faults where
 * they left 86, 43 of spacing for 58, the score's bass and rhythm as they were (docs/07, "The
 * tune or the bass doubled, held and counted"). Room is a choice as Apart is: where the
 * score's bass is a broken chord, a wood-wind over a wood-wind on the lowest part would rather
 * begin a note of the score's harmony off the one note the lowest part is about to be left. It
 * is on until switched: it is asked of nobody but those, and over three takes the wind quartet
 * leaves 10 faults where it left 21, 2 of continuity for 12 (docs/07, "Room for a wood-wind
 * bass, held and counted"). Tremolo is marked, as Marks is: a bowed player is marked the
 * score's tremolo on a note it begins where every note the score strikes for it is shaken,
 * and a bowed tune keeps its own. It is on until switched, since it changes no note either
 * (docs/07, "The score's tremolo, held and counted"). Mute, Bridge, Flutter and Harmonic are
 * marked too: a bowed or a brass player is muted through a bar where the score's own players
 * of its instrument all are, a bowed player bows at the bridge through a bar where the score's
 * bowed strings on its strand all do, a wind is marked the strokes the score's own winds have
 * on every note they strike for it, and a bowed player a natural harmonic where the score's
 * bowed strings on its strand all sound one. They are on until switched, changing no note either
 * (docs/07, "The score's mutes, bridge and winds' strokes, held and counted" and "The score's
 * harmonics, held and counted").
 */

import {
  ARRANGE_HELD,
  ARRANGE_HINTS,
  ARRANGE_LAYERS,
  fittedOf,
  type ArrangeFollow,
  type ArrangeHint,
  type ArrangeLayer,
  type ArrangeMore,
} from "@app/shared-types";
import { HINT_NAMES } from "./ArrangeHintNames";
import { FOLLOW_ON } from "./ArrangeFollowOn";
import ArrangeLayers, { ArrangePresets, LAYER_NAMES, MORE_NAMES } from "./ArrangeLayers";
import { LABEL } from "./Chrome";

export { FOLLOW_ON };

/** The fitted add-ons that stand alone, in place of every other fitted one. */
const ALONE: readonly ArrangeHint[] = ["together", "layers", "more", "line"];

/**
 * `hints` after `hint` is put on or off. A fitted add-on that stands alone is never on beside
 * another fitted one: putting it on puts the rest off, and putting another on puts it off. A
 * held add-on moves no switch but its own and is moved by none.
 */
function switched(hints: readonly ArrangeHint[], hint: ArrangeHint, on: boolean): ArrangeHint[] {
  return ARRANGE_HINTS.filter((one) => {
    if (one === hint) return on;
    if (ARRANGE_HELD.includes(hint) || ARRANGE_HELD.includes(one)) return hints.includes(one);
    if (on && (ALONE.includes(hint) || ALONE.includes(one))) return false;
    return hints.includes(one);
  });
}

/** The fitted add-ons `fitted` beside the held ones `hints` has on. */
function besideHeld(hints: readonly ArrangeHint[], fitted: readonly ArrangeHint[]): ArrangeHint[] {
  return ARRANGE_HINTS.filter((one) =>
    ARRANGE_HELD.includes(one) ? hints.includes(one) : fitted.includes(one),
  );
}

/**
 * The layers `follow` is read as telling the model, by a request in words: the Layers add-on's
 * where it is on, and every one under any other setting, since only that add-on leaves one out.
 * The list `follow` holds while Layers is off is the one it had when last on, and says nothing
 * of the setting.
 */
export function layersTold(follow: ArrangeFollow): readonly ArrangeLayer[] {
  return follow.hints.includes("layers") ? follow.layers : ARRANGE_LAYERS;
}

/**
 * `follow` after an answer in words says the model is told `layers` at `strength` and asked for
 * `more`, `null` for no more than the score. More notes are the More add-on's, so an answer
 * that asks for them puts it on in place of the others. The layers are the Layers add-on's to
 * leave out, so an answer that changes them from those told (`layersTold`) puts that one on;
 * one that leaves them as they stood changes the strength alone, and puts Together on where
 * More was on and is no longer asked for. A held add-on stays as it stood through all of it.
 */
export function askedOf(
  follow: ArrangeFollow,
  layers: readonly ArrangeLayer[],
  strength: number,
  more: ArrangeMore | null,
): ArrangeFollow {
  const fitted = (hints: readonly ArrangeHint[]): ArrangeHint[] => besideHeld(follow.hints, hints);
  if (more !== null) return { hints: fitted(["more"]), layers, strength, more };
  const told = layersTold(follow);
  const moved = ARRANGE_LAYERS.some((layer) => layers.includes(layer) !== told.includes(layer));
  if (moved) return { ...follow, hints: fitted(["layers"]), layers, strength };
  return follow.hints.includes("more")
    ? { ...follow, hints: fitted(fittedOf(FOLLOW_ON.hints)), strength }
    : { ...follow, strength };
}

function listed(told: readonly string[]): string {
  return told.length > 1
    ? [told.slice(0, -1).join(", "), ...told.slice(-1)].join(" and ")
    : told.join("");
}

function percent(strength: number): string {
  return `${Math.round(strength * 100)}%`;
}

/**
 * `follow` as the folded strip says it after the note model's name: " with the score's harmony
 * and rhythm", " with the score's layers (harmony, repeats) at 50%, breath held"; empty with
 * nothing on.
 */
export function saidOf({ hints, layers, strength, more }: ArrangeFollow): string {
  const named = fittedOf(hints).map((hint) =>
    hint === "layers"
      ? `layers (${layers.map((layer) => LAYER_NAMES[layer].label.toLowerCase()).join(", ") || "none told"})`
      : hint === "more"
        ? `more (${MORE_NAMES[more].label.toLowerCase()})`
        : HINT_NAMES[hint].said,
  );
  const told =
    named.length === 0
      ? ""
      : ` with the score's ${named.join(" and ")}${strength === 1 ? "" : ` at ${percent(strength)}`}`;
  const held = hints.filter((hint) => ARRANGE_HELD.includes(hint));
  return held.length === 0
    ? told
    : `${told}, ${listed(held.map((hint) => HINT_NAMES[hint].said))} held`;
}

/**
 * What the note model was shown, and held to, in a run that put `hints` on at `strength`, the
 * Layers add-on told `layers` and the More add-on asked for `more`, as the end of a sentence.
 */
export function shownTo(
  hints: readonly ArrangeHint[],
  layers: readonly ArrangeLayer[] | null,
  strength: number,
  more: ArrangeMore | null,
): string {
  const fitted = fittedOf(hints);
  const told = fitted.flatMap((hint) =>
    hint === "layers"
      ? (layers ?? ARRANGE_LAYERS).map((layer) => LAYER_NAMES[layer].of)
      : [HINT_NAMES[hint].of],
  );
  const held = hints
    .filter((hint) => ARRANGE_HELD.includes(hint))
    .map((hint) => `, ${HINT_NAMES[hint].of}`)
    .join("");
  if (told.length === 0) {
    const out = fitted.length === 0 ? "" : ", the Layers add-on on with every layer left out";
    return `the tune and nothing else of the score${out}${held}`;
  }
  const far =
    strength === 1
      ? ""
      : `, the add-on${fitted.length > 1 ? "s" : ""} put on at ${percent(strength)}`;
  const asked = more === null ? "" : `, each player asked for ${MORE_NAMES[more].of}`;
  return `the tune and ${listed(told)} of the score's accompaniment, not its notes${asked}${far}${held}`;
}

export default function ArrangeHints({
  follow,
  onFollow,
}: {
  readonly follow: ArrangeFollow;
  onFollow(follow: ArrangeFollow): void;
}): JSX.Element {
  return (
    <>
      <span className={LABEL}>Follow the score</span>
      <ArrangePresets follow={follow} onFollow={onFollow} />
      <span className={LABEL}>Add-ons</span>
      {ARRANGE_HINTS.map((hint) => (
        <label
          key={hint}
          title={HINT_NAMES[hint].title}
          className="inline-flex h-7 cursor-pointer items-center gap-1.5 text-[11px] text-shell-text hover:text-shell-lit"
        >
          <input
            type="checkbox"
            checked={follow.hints.includes(hint)}
            onChange={(event) =>
              onFollow({ ...follow, hints: switched(follow.hints, hint, event.target.checked) })
            }
            className="h-3.5 w-3.5 accent-shell-live"
          />
          {HINT_NAMES[hint].label}
        </label>
      ))}
      <ArrangeLayers follow={follow} onFollow={onFollow} />
    </>
  );
}
